import {Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import Downloadicon from '../../../../../../media/billing/ic-download.svg'
import {Dispatch, SetStateAction, useEffect, useState} from 'react'
import {getShareTransactionById} from '../../core/_requests'
import {useAuth} from '../../../../auth'
import Skeleton from 'react-loading-skeleton'
import {
  formatCurrency,
  formatNumber,
  formatTransactionId,
  getCurrencySymbol,
  getFileType,
  parseCurrency,
} from '../../../../../core/_util'
import {useDateFormat} from '../../../../../hooks/useDateFormat'
import {toast} from 'react-toastify'
import {downloadDocument} from '../../../founders-and-promoters/core/_requests'
import {useCurrency} from '../../../../../hooks/useCurrency'
import {TransactionModalType} from '../../core/_models'
import AddSharesModal from './add-shares-modal'
import {useParams} from 'react-router-dom'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../../../hooks/gtmHelpers'
import copyicon from '../../../../../../_metronic/assets/images/svg/copy-svg.svg'

export default function ShareTransactionDetailsModal({
  isActive,
  setIsActive,
  shareTransactionId,
  setIsShareTransactionCreateOrUpdate,
  setIsShareClassDropdown,
  setModalType,
  setIsCreate,
  isShareTransactionCreateOrUpdate,
  setSidebar,
}: {
  isActive: boolean
  setIsActive: () => void
  shareTransactionId: string | null
  setIsShareTransactionCreateOrUpdate: Dispatch<SetStateAction<boolean>>
  isShareTransactionCreateOrUpdate: boolean
  setSidebar: Dispatch<SetStateAction<number>>
  setIsShareClassDropdown: Dispatch<SetStateAction<boolean>>
  setModalType: Dispatch<SetStateAction<TransactionModalType>>
  setIsCreate: Dispatch<SetStateAction<boolean>>
}) {
  const {formatMessage} = useIntl()
  const [shareTransactionDetailsLoading, setShareTransactionDetailsLoading] =
    useState<boolean>(false)
  const [shareTransactionDetails, setShareTransactionDetails] = useState<any>(null)
  const {companyCurrency, companyId, currentUser} = useAuth()
  const {getDateValue} = useDateFormat()
  const [activeModal, setActiveModal] = useState<TransactionModalType>('none')
  const {currencyOptions} = useCurrency()
  const {shareholderId} = useParams()
  const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null)
  const [tagsOpen, setTagsOpen] = useState(false)

  const openModal = (modalType: TransactionModalType) => {
    setActiveModal(modalType)
  }

  const closeModal = () => {
    setActiveModal('none')
  }

  const downloadFile = async (name: string) => {
    try {
      const {
        data: {success, data, errors},
      } = await downloadDocument(name, companyId!)
      if (success) {
        const link = document.createElement('a')
        link.href = data
        link.setAttribute('download', name)
        document.body.appendChild(link)
        toast.success('Document downloaded successfully')
        link.click()
        link.remove()
      } else {
        toast.error(errors[0])
      }
    } catch (error) {
      console.log(error)
    }
  }

  const fetchShareTransactionDetails = async () => {
    if (!shareTransactionId) return
    if (!shareholderId) return
    if (!companyId) return
    setShareTransactionDetailsLoading(true)
    try {
      const {
        data: {success, data, errors},
      } = await getShareTransactionById(companyId, shareholderId, shareTransactionId)
      if (success) {
        setShareTransactionDetails(data)
        setIsShareTransactionCreateOrUpdate(false)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ShareTransactionAccessSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              share_transaction_details: data,
            },
          },
        })
      } else {
        toast.error(errors[0])
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ShareTransactionAccessFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              error: errors[0],
            },
          },
        })
      }
    } catch (error) {
      console.log(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.ShareTransactionAccessFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            error: error,
          },
        },
      })
    } finally {
      setShareTransactionDetailsLoading(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(shareTransactionDetails?.shareTransactionId)
    toast.success('Transaction ID copied to clipboard!')
  }

  useEffect(() => {
    fetchShareTransactionDetails()
  }, [shareTransactionId, isShareTransactionCreateOrUpdate]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Modal
        show={isActive}
        onHide={() => {
          setIsActive()
          setTagsOpen(false)
        }}
        size='lg'
      >
        <Modal.Header closeButton>
          <h2>{formatMessage({id: 'View Transaction'})}</h2>
        </Modal.Header>
        <Modal.Body className='container'>
          <div className='d-md-flex justify-content-between w-100 align-items-center mb-6'>
            <h1 className='fs-4'>{formatMessage({id: 'Share Issuance'})}</h1>
            <div className='h-100 border bg-white d-flex align-items-center rounded'>
              <button
                className='btn btn-primary font-size-12 px-4 py-2 w-100 w-md-auto'
                title='edit transaction'
                onClick={() => {
                  openModal('AddSharesTransaction')
                  setTagsOpen(false)
                }}
              >
                {formatMessage({id: 'Edit transaction'})}
              </button>
            </div>
          </div>

          <div className='row gx-6 justify-content-start mb-4'>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Transaction ID'})}
              </p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareTransactionDetails?.shareTransactionId
                    ? `${
                        shareTransactionDetails?.shareTransactionId
                          ? formatTransactionId(shareTransactionDetails?.shareTransactionId)
                          : '-'
                      }`
                    : '-'}
                  <img
                    src={copyicon}
                    alt='copy-icon'
                    onClick={handleCopy}
                    style={{cursor: 'pointer', marginLeft: '8px', width: '16px', height: '16px'}} // Adjust size as needed
                    title='Copy Transaction ID'
                  />
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Stakeholder'})}</p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareTransactionDetails?.shareholder}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Share Class'})}</p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <div
                  style={{
                    backgroundColor: `rgba(${parseInt(
                      shareTransactionDetails?.shareClass?.tagColor.slice(1, 3),
                      16
                    )}, 
            ${parseInt(shareTransactionDetails?.shareClass?.tagColor.slice(3, 5), 16)}, 
            ${parseInt(shareTransactionDetails?.shareClass?.tagColor.slice(5, 7), 16)}, 
            0.4)`,
                    opacity: '0.8',
                    padding: '5px',
                    width: '70%',
                    borderRadius: '5px',
                  }}
                >
                  <p className='font-size-14 m-0 font-weight-normal'>
                    {shareTransactionDetails?.shareClass?.className}
                  </p>
                </div>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Certificate Code'})}
              </p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareTransactionDetails?.certificateCode ?? '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Shares'})}</p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 mb-0'>{shareTransactionDetails?.numberOfShares}</p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Price Per Share'})}
              </p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  <span ref={(node) => setModalContainer(node)}>
                    <OverlayTrigger
                      placement='bottom'
                      overlay={
                        <Tooltip id='tooltip'>
                          <p className='m-0'>{`${companyCurrency} 
                     ${formatCurrency(
                       shareTransactionDetails?.pricePerShare,
                       companyCurrency
                     )}`}</p>
                        </Tooltip>
                      }
                      container={modalContainer}
                    >
                      <p className='font-size-13 mb-0'>
                        {shareTransactionDetails?.pricePerShare ? (
                          <>
                            {`${companyCurrency} ${getCurrencySymbol(
                              companyCurrency,
                              currencyOptions
                            )}
                            ${parseCurrency(shareTransactionDetails?.pricePerShare)}`}
                          </>
                        ) : (
                          '-'
                        )}
                      </p>
                    </OverlayTrigger>
                  </span>
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Total Investment'})}
              </p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  <span ref={(node) => setModalContainer(node)}>
                    <OverlayTrigger
                      placement='bottom'
                      overlay={
                        <Tooltip id='tooltip'>
                          <p className='m-0'>{`${companyCurrency} 
                      ${formatCurrency(
                        shareTransactionDetails?.totalTransactionValue,
                        companyCurrency
                      )}`}</p>
                        </Tooltip>
                      }
                      container={modalContainer}
                    >
                      <p className='font-size-13 mb-0'>
                        {shareTransactionDetails?.totalTransactionValue ? (
                          <>
                            {companyCurrency} {getCurrencySymbol(companyCurrency, currencyOptions)}
                            {parseCurrency(shareTransactionDetails?.totalTransactionValue)}
                          </>
                        ) : (
                          '-'
                        )}
                      </p>
                    </OverlayTrigger>
                  </span>
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Date of Issuance'})}
              </p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {getDateValue(shareTransactionDetails?.dateOfIssue)}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Vote Per Share'})}
              </p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareTransactionDetails?.votesPerShare
                    ? `${formatNumber(shareTransactionDetails?.votesPerShare)}`
                    : '0'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Exchange Rate'})}</p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareTransactionDetails?.exchangeRate
                    ? `${formatNumber(shareTransactionDetails?.exchangeRate)}`
                    : '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Tags'})}</p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <div className='d-flex align-items-center flex-wrap gap-1 font-size-12'>
                  {shareTransactionDetails?.tags
                    ? shareTransactionDetails?.tags
                        .split(',')
                        .slice(0, 3)
                        .map((tag: string, index: number) => {
                          return (
                            <div
                              className='m-0 font-weight-normal px-2 py-1 border border-primary-subtle rounded-1'
                              key={index}
                            >
                              {tag}
                            </div>
                          )
                        })
                    : '-'}
                  {shareTransactionDetails?.tags && !tagsOpen &&
                    shareTransactionDetails?.tags.split(',').length > 3 && (
                      <div
                        className='cursor-pointer tags'
                        onClick={() => {
                          setTagsOpen(true)
                        }}
                      >
                        +{shareTransactionDetails?.tags.split(',').slice(3).length}
                      </div>
                    )}
                  {tagsOpen && (
                    <>
                      {shareTransactionDetails?.tags
                        ? shareTransactionDetails?.tags
                            .split(',')
                            .slice(3)
                            .map((tag: string, index: number) => {
                              return (
                                <div
                                  className='m-0 font-weight-normal px-2 py-1 border border-primary-subtle rounded-1'
                                  key={index}
                                >
                                  {tag}
                                </div>
                              )
                            })
                        : '-'}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Comments'})}</p>
              {shareTransactionDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareTransactionDetails?.comments
                    ? `${
                        shareTransactionDetails?.comments ? shareTransactionDetails?.comments : '-'
                      }`
                    : '-'}
                </p>
              )}
            </div>
          </div>

          <div className='seperator bg-dark border mb-6'></div>

          <div className='d-md-flex justify-content-between w-100 align-items-center mb-6'>
            <h1 className='fs-4'>{formatMessage({id: 'Documents'})}</h1>
          </div>

          {shareTransactionDetailsLoading ? (
            <div className='d-flex gap-3'>
              <div className='card p-3 border border-muted'>
                <Skeleton
                  height={30}
                  width={200}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />

                <Skeleton
                  height={60}
                  width={200}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div className='card p-3 border border-muted'>
                <Skeleton
                  height={30}
                  width={200}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
                <Skeleton
                  height={60}
                  width={200}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
            </div>
          ) : shareTransactionDetails?.documents.length === 0 ? (
            <p className='font-size-14 m-0 font-weight-normal text-gray'>
              {formatMessage({id: 'No Documents added to this issuance'})}
            </p>
          ) : (
            <div className='row'>
              {shareTransactionDetails?.documents.map((file: any) => (
                <div className='col-12 col-md-6 col-lg-4 mb-4' key={file.documentId}>
                  <div className='card p-3 border border-muted'>
                    <img
                      src={getFileType(file.name)}
                      alt={file.name}
                      style={{height: '35px', width: '35px'}}
                    />
                    <div className='d-flex justify-content-between align-items-center'>
                      <p className='p-0 m-0 w-75'>{file?.name?.split('/').pop()}</p>
                      <img
                        className='cursor-pointer'
                        src={Downloadicon}
                        alt='download'
                        onClick={() => downloadFile(file.name)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
      {activeModal === 'AddSharesTransaction' && (
        <AddSharesModal
          isActive={true}
          setIsActive={closeModal}
          shareTransactionId={shareTransactionId}
          setIsShareTransactionCreateOrUpdate={setIsShareTransactionCreateOrUpdate}
          setSidebar={setSidebar}
          setModalType={setModalType}
          setIsCreate={setIsCreate}
          openModal={openModal}
          setIsShareClassDropdown={setIsShareClassDropdown}
        />
      )}
    </>
  )
}
